import { action, computed, makeObservable, observable, untracked } from 'mobx';
import type { PhoneCallDto, AmlPhoneCallDto } from '@mark43/cad-api';
import {
    EventPanelData,
    EntityPanelManager,
} from '../panels/entity-panels/monitored-entities/state/entity-panel-manager';
import { isValidLatLng } from '../location/helpers';
import type { AmlCallView, PhoneCallView, PhoneCallNotification } from './types';
import { AmlLocation } from '@mark43/cad-api';

const MAX_LOCATIONS_IN_AML_HISTORY = 8;

function getPhoneCallAddressDisplay(call: PhoneCallDto): string | undefined {
    let display = call.callerAddress;

    if (call.subscriberBuildingName) {
        display += `, Building Name: ${call.subscriberBuildingName}`;
    }

    if (call.subscriberBuildingFloor) {
        display += `, Building Floor: ${call.subscriberBuildingFloor}`;
    }

    if (call.subscriberUnitNumber) {
        display += `, Unit Number: ${call.subscriberUnitNumber}`;
    }

    return display;
}

export function phoneCallWithNormalizedPhoneCallDisplay(
    phoneCall: PhoneCallDto,
    isHistoric?: boolean
): PhoneCallView {
    const [lat, lng] = (phoneCall.callerGps ?? '').split(',').map((str) => str.trim());
    const formatClassOfServiceAndUncertaintyConfidence = () => {
        const { classOfService, uncertaintyFactorFeet, confidencePercentage } = phoneCall;

        if ([classOfService, uncertaintyFactorFeet, confidencePercentage].some((s) => !!s)) {
            const uncertainty = uncertaintyFactorFeet ? `${uncertaintyFactorFeet}ft` : undefined;
            const confidence = confidencePercentage ? `${confidencePercentage}%` : undefined;
            const uncertaintyConfidenceString = [uncertainty, confidence]
                .filter((s) => !!s)
                .join(', ');

            return (
                (classOfService ?? '') +
                (uncertaintyConfidenceString && classOfService ? '– ' : '') +
                uncertaintyConfidenceString
            );
        }
    };

    return {
        ...phoneCall,
        isHistoric,
        normalizedPhoneCallDisplay: {
            id: phoneCall.id,
            classOfService: phoneCall.classOfService,
            classOfServiceAndUncertaintyConfidenceDisplay: formatClassOfServiceAndUncertaintyConfidence(),
            pilotNumberDisplay: phoneCall.pilotNumberDisplay,
            callerName: phoneCall.reportingParty,
            callerPhoneNumber: phoneCall.phoneNumberDisplay,
            callerAddress: phoneCall.callerAddress,
            callerAddressDisplay: getPhoneCallAddressDisplay(phoneCall),
            subscriberBuildingName: phoneCall.subscriberBuildingName,
            subscriberBuildingFloor: phoneCall.subscriberBuildingFloor,
            subscriberUnitNumber: phoneCall.subscriberUnitNumber,
            callerGps: phoneCall.callerGps,
            latitude: lat,
            longitude: lng,
            latFloat: lat ? parseFloat(lat) : undefined,
            lngFloat: lng ? parseFloat(lng) : undefined,
            hasValidLatLng: isValidLatLng(lat, lng),
        },
    };
}
export function getLastAmlCallLocation(locations: AmlLocation[]): AmlLocation | undefined {
    return locations.length ? locations[locations.length - 1] : undefined;
}

export function amlCallWithNormalizedPhoneCallDisplay(amlCall: AmlPhoneCallDto): AmlCallView {
    const lastLocation = getLastAmlCallLocation(amlCall.locations);
    const lat = lastLocation?.latitude?.toString();
    const lng = lastLocation?.longitude?.toString();

    return {
        ...amlCall,
        normalizedPhoneCallDisplay: {
            id: amlCall.id,
            classOfService: undefined,
            classOfServiceAndUncertaintyConfidenceDisplay: undefined,
            pilotNumberDisplay: undefined,
            callerName: amlCall.callerName,
            callerPhoneNumber: amlCall.phoneNumberDisplay,
            callerAddress: lastLocation?.rgAddress,
            callerGps: lat && lng ? [lat, lng].join(', ') : undefined,
            latitude: lat,
            longitude: lng,
            latFloat: lat ? parseFloat(lat) : undefined,
            lngFloat: lng ? parseFloat(lng) : undefined,
            hasValidLatLng: isValidLatLng(lat, lng),
        },
    };
}

const phoneCallViewNotificationKeys = [
    'callerName',
    'callerPhoneNumber',
    'callerAddress',
    'callerGps',
] as const;

const phoneCallViewKeyToPhoneCallNotification: Record<
    typeof phoneCallViewNotificationKeys[number],
    PhoneCallNotification
> = {
    callerName: 'CALLER_NAME',
    callerPhoneNumber: 'CALLER_PHONE_NUMBER',
    callerAddress: 'CALLER_ADDRESS',
    callerGps: 'CALLER_GPS',
};

export class PhoneCallsStore {
    @observable
    private data: {
        phoneCalls: Record<number, PhoneCallView>;
        amlCalls: Record<number, AmlCallView>;
        phoneCallNotifications: Record<number, Partial<Record<PhoneCallNotification, true>>>;
    } = { phoneCalls: {}, amlCalls: {}, phoneCallNotifications: {} };
    private eventPanelManager: EntityPanelManager;

    constructor(props: {
        eventPanelManager: EntityPanelManager;
        initialPhoneCalls: PhoneCallDto[];
    }) {
        this.eventPanelManager = props.eventPanelManager;
        this.data.phoneCalls = Object.fromEntries(
            props.initialPhoneCalls.map((phoneCallDto) => [
                phoneCallDto.id,
                phoneCallWithNormalizedPhoneCallDisplay(phoneCallDto),
            ])
        );

        makeObservable(this);
    }

    public getPhoneCall(phoneCallId: number): PhoneCallView | undefined {
        return this.data.phoneCalls[phoneCallId];
    }

    public getPhoneCallByPhoneNumberId(phoneNumberId: number): PhoneCallView | undefined {
        return Object.values(this.data.phoneCalls).find(
            (call) => call.phoneNumberId === phoneNumberId
        );
    }

    public getAmlCallById(amlCallId: number): AmlCallView | undefined {
        return Object.values(this.data.amlCalls).find((call) => call.id === amlCallId);
    }

    public getAmlCallByPhoneNumberId(amlPhoneNumberId: number): AmlCallView | undefined {
        return this.data.amlCalls[amlPhoneNumberId];
    }

    @action
    public upsertPhoneCall(phoneCall: PhoneCallDto): void {
        const existingPhoneCallView = this.data.phoneCalls[phoneCall.phoneNumberId!];
        const newPhoneCallView = phoneCallWithNormalizedPhoneCallDisplay(phoneCall);
        if (existingPhoneCallView) {
            for (const phoneCallNotificationKey of phoneCallViewNotificationKeys) {
                if (
                    newPhoneCallView.normalizedPhoneCallDisplay[phoneCallNotificationKey!] !==
                    existingPhoneCallView.normalizedPhoneCallDisplay[phoneCallNotificationKey]
                ) {
                    const notificationsForPhoneNumberId = this.data.phoneCallNotifications[
                        newPhoneCallView.phoneNumberId!
                    ];
                    if (!notificationsForPhoneNumberId) {
                        this.data.phoneCallNotifications[newPhoneCallView.phoneNumberId!] = {};
                    }
                    const notification =
                        phoneCallViewKeyToPhoneCallNotification[phoneCallNotificationKey];
                    this.data.phoneCallNotifications[newPhoneCallView.phoneNumberId!][
                        notification
                    ] = true;
                }
            }
        }
        this.data.phoneCalls[phoneCall.phoneNumberId!] = newPhoneCallView;
    }

    public upsertHistoricPhoneCall(phoneCall: PhoneCallDto): void {
        if (this.data.phoneCalls[phoneCall.phoneNumberId!]) return;
        const historicPhoneCallView = phoneCallWithNormalizedPhoneCallDisplay(phoneCall, true);
        this.data.phoneCalls[phoneCall.phoneNumberId!] = historicPhoneCallView;
    }

    @action
    public upsertAmlCall(amlCall: AmlPhoneCallDto): void {
        const existingAmlCall = this.data.amlCalls[amlCall.phoneNumberId];
        let newAmlCallView = amlCallWithNormalizedPhoneCallDisplay(amlCall);

        if (existingAmlCall) {
            const transformedLocations = [
                ...existingAmlCall.locations,
                ...newAmlCallView.locations,
            ];
            const locations =
                existingAmlCall.locations.length >= MAX_LOCATIONS_IN_AML_HISTORY
                    ? transformedLocations.slice(-MAX_LOCATIONS_IN_AML_HISTORY, -1)
                    : transformedLocations;

            newAmlCallView = {
                ...newAmlCallView,
                locations,
            };
        }
        this.data.amlCalls[amlCall.phoneNumberId] = newAmlCallView;
    }

    @action removePhoneCallNotifications(phoneNumberId: number): void {
        const existingNotifications = this.data.phoneCallNotifications[phoneNumberId];
        for (const key in existingNotifications) {
            delete existingNotifications[key as PhoneCallNotification];
        }
    }

    public getPhoneCallNotification(
        phoneNumberId: number,
        notification: PhoneCallNotification
    ): boolean {
        let notifications: typeof this.data.phoneCallNotifications;
        untracked(() => {
            notifications = this.data.phoneCallNotifications;
        });
        return !!notifications![phoneNumberId]?.[notification];
    }

    public isDuplicatPhoneCallMessage(phoneCall: PhoneCallDto): boolean {
        const existingPhoneCall = this.data.phoneCalls[phoneCall.phoneNumberId!];
        if (existingPhoneCall && existingPhoneCall.rmsEventId === phoneCall.rmsEventId) {
            return true;
        }
        return false;
    }

    public getMatchingOpenEventPanel(phoneCall: PhoneCallDto): EventPanelData | undefined {
        const eventPanelDatas = this.eventPanelManager.getAllEventPanels();
        const matchingEventPanel = eventPanelDatas.find((eventPanel) => {
            const formValues = eventPanel.form.getValues();
            return (
                eventPanel.phoneCall?.phoneNumberId === phoneCall.phoneNumberId ||
                formValues.phoneCall?.phoneNumberId === phoneCall.phoneNumberId
            );
        });
        return matchingEventPanel;
    }

    @computed
    get activePhoneCallsList(): PhoneCallView[] {
        return Object.values(this.data.phoneCalls)
            .slice()
            .filter((phoneCallView) => !phoneCallView.isHistoric)
            .sort((a, b) => {
                return a.id > b.id ? -1 : 1;
            });
    }

    @computed
    get amlCallsList(): AmlCallView[] {
        return Object.values(this.data.amlCalls);
    }
}
